import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getSocket } from "../api/socketClient";
import * as ownerApi from "../api/notifications/notificationApi";
import * as portalApi from "../api/notifications/notificationPortalApi";

const NotificationContext = createContext(null);

function resolveUserType(explicit) {
  if (explicit === "staff" || explicit === "owner") return explicit;
  if (typeof window === "undefined") return "owner";
  const path = window.location.pathname || "";
  if (path.startsWith("/staff-panel")) return "staff";
  if (path.startsWith("/student-panel")) return null; // no student API yet
  return "owner";
}

/**
 * @param {{ userType?: "owner"|"staff", isPortal?: boolean, enabled?: boolean, children: React.ReactNode }} props
 */
export function NotificationProvider({
  userType: userTypeProp,
  isPortal = false,
  enabled = true,
  children,
}) {
  const userType = useMemo(() => {
    if (userTypeProp === "staff" || userTypeProp === "owner") return userTypeProp;
    if (isPortal) return "staff";
    return resolveUserType(null);
  }, [userTypeProp, isPortal]);

  const active = enabled && (userType === "owner" || userType === "staff");
  const isStaff = userType === "staff";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    if (!active) return;
    try {
      const data = isStaff
        ? await portalApi.fetchUnreadCountPortal()
        : await ownerApi.fetchUnreadCount();
      const count = data?.data?.count ?? 0;
      if (mountedRef.current) setUnreadCount(Number(count) || 0);
    } catch (err) {
      console.warn("[NotificationContext] unread-count failed:", err?.message);
    }
  }, [active, isStaff]);

  const fetchNotifications = useCallback(
    async (params = {}) => {
      if (!active) return;
      setLoading(true);
      setError(null);
      try {
        const data = isStaff
          ? await portalApi.fetchNotificationsPortal(params)
          : await ownerApi.fetchNotifications(params);
        const items = data?.data?.items || [];
        const pag = data?.data?.pagination || {
          page: 1,
          limit: 20,
          total: items.length,
          pages: 1,
        };
        if (mountedRef.current) {
          setNotifications(items);
          setPagination(pag);
        }
      } catch (err) {
        console.warn("[NotificationContext] fetch failed:", err?.message);
        if (mountedRef.current) {
          setError(err?.response?.data?.message || "Failed to load notifications");
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [active, isStaff]
  );

  const markAsRead = useCallback(
    async (id) => {
      if (!active || !id) return;
      try {
        if (isStaff) await portalApi.markNotificationReadPortal(id);
        else await ownerApi.markNotificationRead(id);
        if (mountedRef.current) {
          setNotifications((prev) =>
            prev.map((n) =>
              String(n._id) === String(id) ? { ...n, isRead: true } : n
            )
          );
          setUnreadCount((c) => Math.max(0, c - 1));
        }
      } catch (err) {
        console.warn("[NotificationContext] markAsRead failed:", err?.message);
      }
    },
    [active, isStaff]
  );

  const markAllAsRead = useCallback(async () => {
    if (!active) return;
    try {
      if (isStaff) await portalApi.markAllNotificationsReadPortal();
      else await ownerApi.markAllNotificationsRead();
      if (mountedRef.current) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.warn("[NotificationContext] markAllAsRead failed:", err?.message);
    }
  }, [active, isStaff]);

  const removeNotification = useCallback(
    async (id) => {
      if (!active || !id) return;
      try {
        if (isStaff) await portalApi.deleteNotificationPortal(id);
        else await ownerApi.deleteNotification(id);
        if (mountedRef.current) {
          setNotifications((prev) => {
            const target = prev.find((n) => String(n._id) === String(id));
            if (target && !target.isRead) {
              setUnreadCount((c) => Math.max(0, c - 1));
            }
            return prev.filter((n) => String(n._id) !== String(id));
          });
        }
      } catch (err) {
        console.warn("[NotificationContext] delete failed:", err?.message);
      }
    },
    [active, isStaff]
  );

  // Initial load
  useEffect(() => {
    if (!active) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    fetchNotifications({ page: 1, limit: 20 });
    refreshUnreadCount();
  }, [active, fetchNotifications, refreshUnreadCount]);

  // Real-time: listen for "notification" on personal room (joined server-side)
  useEffect(() => {
    if (!active) return undefined;

    const socket = getSocket(userType);

    const onNotification = (notif) => {
      if (!notif || !notif._id) return;
      setNotifications((prev) => {
        if (prev.some((n) => String(n._id) === String(notif._id))) return prev;
        return [notif, ...prev];
      });
      setUnreadCount((c) => c + (notif.isRead ? 0 : 1));
    };

    // Server emits this after a user reads a conversation in chat (join_room),
    // so any bell notifications generated for messages they've now seen get
    // cleared here too, instead of lingering as "unread" in the panel.
    const onBulkRead = (payload) => {
      const ids = new Set((payload?.ids || []).map((id) => String(id)));
      if (!ids.size) return;

      let decrement = 0;
      setNotifications((prev) =>
        prev.map((n) => {
          if (ids.has(String(n._id)) && !n.isRead) {
            decrement += 1;
            return { ...n, isRead: true };
          }
          return n;
        })
      );
      if (decrement > 0) {
        setUnreadCount((c) => Math.max(0, c - decrement));
      }
    };

    socket.on("notification", onNotification);
    socket.on("notifications_bulk_read", onBulkRead);

    return () => {
      socket.off("notification", onNotification);
      socket.off("notifications_bulk_read", onBulkRead);
    };
  }, [active, userType]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      error,
      pagination,
      userType,
      isStaff,
      active,
      fetchNotifications,
      refreshUnreadCount,
      markAsRead,
      markAllAsRead,
      removeNotification,
    }),
    [
      notifications,
      unreadCount,
      loading,
      error,
      pagination,
      userType,
      isStaff,
      active,
      fetchNotifications,
      refreshUnreadCount,
      markAsRead,
      markAllAsRead,
      removeNotification,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      error: null,
      pagination: { page: 1, limit: 20, total: 0, pages: 1 },
      userType: "owner",
      isStaff: false,
      active: false,
      fetchNotifications: async () => {},
      refreshUnreadCount: async () => {},
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      removeNotification: async () => {},
    };
  }
  return ctx;
}

export default NotificationContext;